import { Component, Inject, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { WebSerialScaleService } from '../../../../services/web-serial-scale.service';
 
export interface FoamMeasureDialogData {
  calculatedFoamWeight?: number;
  measuredWeight?: number;
  weightComments?: string;
  measurementTolerance?: number; // Tolerance in grams (absolute value, not percentage)
}
 
export interface FoamMeasureDialogResult {
  measuredWeight?: number;
  weightComments?: string;
}
 
@Component({
  selector: 'app-foam-measure-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule
  ],
  templateUrl: './foam-measure-dialog.component.html',
  styleUrls: ['./foam-measure-dialog.component.css']
})
export class FoamMeasureDialogComponent implements OnDestroy {
  measuredWeight?: number;
  weightComments?: string;
  showWeightCommentsSection = false;
 
  // Scale reading state
  isMeasuringWeight = false;
  scaleError?: string;
  private abortController?: AbortController;
 
  constructor(
    public dialogRef: MatDialogRef<FoamMeasureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FoamMeasureDialogData,
    private scaleService: WebSerialScaleService
  ) {
    // Pre-populate with existing values
    this.measuredWeight = data.measuredWeight;
    this.weightComments = data.weightComments || '';
   
    // Check if weight comments section should be visible on init
    if (this.measuredWeight !== undefined && this.measuredWeight !== null) {
      setTimeout(() => {
        if (!this.isWeightAcceptable) {
          this.showWeightCommentsSection = true;
        }
      });
    }
   
    // Auto-start scale reading when dialog opens (matches WPF behavior)
    this.startScaleReading();
  }
 
  ngOnDestroy(): void {
    this.abortController?.abort();
  }
 
  /**
   * Matches WPF line 137: MeasuredMaterialsHelper.IsWeightAcceptable
   * Tolerance is absolute value in grams, not percentage
   */
  get isWeightAcceptable(): boolean {
    if (!this.data.calculatedFoamWeight || this.measuredWeight === undefined || this.measuredWeight === null) {
      return true;
    }
    const tolerance = this.data.measurementTolerance ?? 0.5; // Default 0.5g
    const difference = Math.abs(this.measuredWeight - this.data.calculatedFoamWeight);
    return difference <= tolerance;
  }
 
  get isValid(): boolean {
    const hasBasicData = (
      this.measuredWeight !== undefined &&
      this.measuredWeight !== null &&
      !this.isMeasuringWeight
    );
 
    if (!hasBasicData) return false;
 
    // If weight comments section is visible, comments must be provided
    if (this.showWeightCommentsSection) {
      if (!this.weightComments || !this.weightComments.trim()) {
        return false;
      }
    }
 
    return true;
  }
 
  /**
   * Matches WPF lines 457-485: MeasureFoam() method
   * Automatically starts reading from scale when dialog opens
   */
  private async startScaleReading(): Promise<void> {
    this.isMeasuringWeight = true;
    this.scaleError = undefined;
    this.abortController = new AbortController();
 
    try {
      const result = await this.scaleService.readWeightFromGrantedPort(this.abortController.signal);
     
      if (result.success && result.stable) {
        this.measuredWeight = result.weight;
        this.onWeightChanged();
      } else if (result.errorMessage) {
        this.scaleError = result.errorMessage;
      }
    } catch (error) {
      console.error('Scale reading error:', error);
      this.scaleError = error instanceof Error ? error.message : 'Unknown scale error';
    } finally {
      this.isMeasuringWeight = false;
    }
  }
 
  /**
   * Retry scale reading (user clicks button after error or cancellation)
   */
  async retryScaleReading(): Promise<void> {
    this.abortController?.abort();
    await this.startScaleReading();
  }
 
  onWeightChanged(): void {
    // Check weight tolerance when weight is changed
    if (this.measuredWeight !== undefined && this.measuredWeight !== null) {
      if (!this.isWeightAcceptable) {
        this.showWeightCommentsSection = true;
      } else {
        this.showWeightCommentsSection = false;
        this.weightComments = '';
      }
    }
  }
 
  onCancel(): void {
    this.abortController?.abort();
    this.dialogRef.close();
  }
 
  onSave(): void {
    if (this.isValid) {
      const result: FoamMeasureDialogResult = {
        measuredWeight: this.measuredWeight,
        weightComments: this.weightComments
      };
      this.dialogRef.close(result);
    }
  }
}